import { useState } from 'react';
import { votePoll, removeVote, closePoll } from '@/lib/api';
import type { Poll } from '@goodchat/shared/types';

interface PollCardProps {
  messageId: string;
  poll: Poll;
  isAuthor: boolean;
  onPollUpdate?: (poll: Poll) => void;
}

export function PollCard({ messageId, poll, isAuthor, onPollUpdate }: PollCardProps) {
  const [currentPoll, setCurrentPoll] = useState(poll);
  const [loading, setLoading] = useState(false);

  const maxVotes = Math.max(...currentPoll.options.map((o) => o.voteCount), 1);
  const hasVoted = currentPoll.myVotes.length > 0;

  async function handleVote(optionId: string) {
    if (currentPoll.isClosed || loading) return;
    setLoading(true);
    try {
      const isSelected = currentPoll.myVotes.includes(optionId);
      const result = isSelected
        ? await removeVote(messageId, optionId)
        : await votePoll(messageId, optionId);
      setCurrentPoll(result.poll);
      onPollUpdate?.(result.poll);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  async function handleClose() {
    if (loading) return;
    setLoading(true);
    try {
      const result = await closePoll(messageId);
      setCurrentPoll(result.poll);
      onPollUpdate?.(result.poll);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  // Update from WS events
  if (poll.id === currentPoll.id && poll.totalVotes !== currentPoll.totalVotes) {
    setCurrentPoll(poll);
  }

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3 max-w-md">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">📊</span>
        <span className="font-medium text-gray-900 dark:text-gray-100">{currentPoll.question}</span>
      </div>

      {currentPoll.isAnonymous && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Anonymous poll</p>
      )}
      {currentPoll.allowMultiple && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Multiple choices allowed</p>
      )}

      <div className="space-y-2">
        {currentPoll.options.map((option) => {
          const isSelected = currentPoll.myVotes.includes(option.id);
          const percentage = currentPoll.totalVotes > 0
            ? Math.round((option.voteCount / currentPoll.totalVotes) * 100)
            : 0;

          return (
            <button
              key={option.id}
              onClick={() => handleVote(option.id)}
              disabled={currentPoll.isClosed || loading}
              className={`w-full text-left relative rounded-md overflow-hidden border transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
              } ${currentPoll.isClosed ? 'cursor-default' : 'cursor-pointer'}`}
            >
              {/* Progress bar background */}
              {(hasVoted || currentPoll.isClosed) && (
                <div
                  className={`absolute inset-y-0 left-0 transition-all ${
                    isSelected ? 'bg-blue-100 dark:bg-blue-800/30' : 'bg-gray-100 dark:bg-gray-700/30'
                  }`}
                  style={{ width: `${percentage}%` }}
                />
              )}

              <div className="relative px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isSelected && <span className="text-blue-600 dark:text-blue-400">✓</span>}
                  <span className="text-sm text-gray-900 dark:text-gray-100">{option.text}</span>
                </div>
                {(hasVoted || currentPoll.isClosed) && (
                  <span className="text-xs text-gray-600 dark:text-gray-300 ml-2 shrink-0">
                    {option.voteCount} ({percentage}%)
                  </span>
                )}
              </div>

              {/* Voter avatars (non-anonymous only) */}
              {!currentPoll.isAnonymous && option.voters && option.voters.length > 0 && (hasVoted || currentPoll.isClosed) && (
                <div className="relative px-3 pb-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {option.voters.map((v) => v.displayName).join(', ')}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
        <span>{currentPoll.totalVotes} vote{currentPoll.totalVotes !== 1 ? 's' : ''}</span>
        {currentPoll.isClosed ? (
          <span className="text-red-500">Poll closed</span>
        ) : isAuthor ? (
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-red-500 hover:text-red-600 font-medium"
          >
            Close poll
          </button>
        ) : null}
      </div>
    </div>
  );
}
